/**
 * useProposalVoters — DAO-FOUND-03 (Phase 3)
 *
 * Returns the list of DAO member addresses that have confirmed (voted on)
 * a given proposal.
 *
 * Strategy:
 *   Phase A — batch-read ownersOfCycle(cycle, 0..14) from Election.sol
 *             to get the 15 member addresses for that cycle.
 *   Phase B — batch-read isConfirmed(txIndex, owner) from DAO.sol
 *             for each of the 15 addresses.
 *   Result   — filter to addresses where isConfirmed === true.
 *
 * Both batches use allowFailure: false (Phase 1 invariant).
 * No query cache override (feedback_query_staletime).
 *
 * @param txIndex    — the chain tx index of the proposal
 * @param cycleCount — cycle_count from DaoResponseDto (backend)
 * @param enabled    — set to false until the voter dropdown is first opened
 */

import { useMemo } from "react";
import { useReadContracts } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { DAO_ABI } from "@/contract/abi/dao";
import { ELECTION_ABI } from "@/contract/abi/election";
import { ContractAddress } from "@/contract/addresses";
import { $api } from "@/lib/api-client";

const QUORUM = 15;

export interface ProposalVoter {
  address: string;
  nickname: string | null;
}

function buildOwnerReads(cycleCount: number) {
  return Array.from({ length: QUORUM }, (_, i) => ({
    abi: ELECTION_ABI,
    address: ContractAddress.ELECTION,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "ownersOfCycle" as const,
    args: [BigInt(cycleCount), BigInt(i)] satisfies [bigint, bigint],
  }));
}

function buildConfirmedReads(txIndex: number, owners: readonly string[]) {
  return owners.map((owner) => ({
    abi: DAO_ABI,
    address: ContractAddress.DAO,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "isConfirmed" as const,
    args: [BigInt(txIndex), owner] satisfies [bigint, string],
  }));
}

export function useProposalVoters(
  txIndex: number,
  cycleCount: number,
  enabled = false,
) {
  // Phase A: get the 15 owner addresses for this cycle
  const ownerReads = useReadContracts({
    contracts: buildOwnerReads(cycleCount),
    allowFailure: false,
    query: { enabled, retry: false },
  });

  const owners = ownerReads.data;

  // Phase B: check isConfirmed for each owner — only runs once owners are loaded
  const confirmedReads = useReadContracts({
    contracts: owners ? buildConfirmedReads(txIndex, owners) : [],
    allowFailure: false,
    query: { enabled: enabled && !!owners, retry: false },
  });

  const voterAddresses: string[] = useMemo(
    () =>
      owners && confirmedReads.data
        ? owners.filter((_, i) => confirmedReads.data?.[i] === true)
        : [],
    [owners, confirmedReads.data],
  );

  // Phase C: batch nickname resolver for the filtered voters
  const nicknameQuery = $api.useQuery(
    "get",
    "/user/by-addresses",
    { params: { query: { a: voterAddresses } } },
    { enabled: voterAddresses.length > 0 },
  );

  const voters: ProposalVoter[] = useMemo(() => {
    const nameByAddr = new Map<string, string | null>();
    for (const u of nicknameQuery.data?.data ?? []) {
      nameByAddr.set(u.wallet_address.toLowerCase(), u.nickname ?? null);
    }
    return voterAddresses.map((addr) => ({
      address: addr,
      nickname: nameByAddr.get(addr.toLowerCase()) ?? null,
    }));
  }, [voterAddresses, nicknameQuery.data]);

  return {
    voters,
    // Bug 2026-05-23: nicknameQuery has `enabled: voterAddresses.length > 0`,
    // and TanStack Query reports `isLoading: true` on any query that has
    // never received data (including ones that were never enabled). When the
    // proposal has 0 voters, the dropdown got pinned to "loading..." forever
    // because nicknameQuery.isLoading stayed truthy. Only include the
    // nickname loading state when there are actually voters to resolve.
    isLoading:
      ownerReads.isLoading ||
      confirmedReads.isLoading ||
      (voterAddresses.length > 0 && nicknameQuery.isLoading),
    isError: ownerReads.isError || confirmedReads.isError,
  };
}
